import * as Yup from 'yup';
import { isValidNumber } from 'libphonenumber-js';
import { yupToFormErrors } from 'formik';
import ButtonModel, {
  ButtonType,
  HandoverButtonConfig,
  PageButtonConfig,
  PhoneNumberButtonConfig,
  UrlButtonConfig,
  DEFAULT_WEBVIEW_HEIGHT,
  WebviewHeightRatio,
} from '@mm/state/model/ButtonModel';
import TagModel from '@mm/state/model/TagModel';
import { URL_REGEX } from '../../../constants/validations';

export type CreateEditButtonFormValues = {
  buttonName: string;
  phoneNumber: string;
  url: string;
  webviewSize: WebviewHeightRatio;
  appId: string;
  pageId: string;
  selectedButtonType: ButtonType;
  tags: TagModel[];
  metadata: string;
};

export type CreateEditButtonFormValuesNew = {
  name: string;
  phone: string;
  url: string;
  appId: string;
  pageId: string;
  selectedButtonType: ButtonType;
  tags: TagModel[];
};

const getInitialValues = (
  button: ButtonModel | undefined,
): CreateEditButtonFormValues => {
  const buttonName = button && button.name ? button.name : '';
  const url = button ? (button.config as UrlButtonConfig).url : '';
  const webviewSize = button
    ? (button.config as UrlButtonConfig).webviewHeightRatio
    : null;
  const appId = button ? (button.config as HandoverButtonConfig).appId : '';
  const tags = button && button.tags ? button.tags : [];
  const phoneNumber = button
    ? (button.config as PhoneNumberButtonConfig).phoneNumber
    : '';
  const selectedButtonType = button ? button.type : ButtonType.PAGE;
  const pageId =
    button && (button.config as PageButtonConfig).pageId !== -1
      ? (button.config as PageButtonConfig).pageId
      : '';
  const metadata = (button?.config as HandoverButtonConfig)?.metadata || '';

  return {
    buttonName,
    phoneNumber,
    url,
    webviewSize: webviewSize || DEFAULT_WEBVIEW_HEIGHT,
    appId,
    pageId: String(pageId),
    selectedButtonType,
    tags,
    metadata,
  };
};

const getValidationSchema = (
  buttonType: ButtonType,
  disregardLayout = false,
) => {
  const commonFields = {
    buttonName: Yup.string().trim().required('Name Required'),
  };

  if (buttonType === ButtonType.CALL) {
    return Yup.object().shape({
      ...commonFields,
      phoneNumber: Yup.string()
        .required('Phone Number Required')
        .test('is-phone-number', 'Invalid Phone Number', value => {
          if (!value) {
            return false;
          }
          return isValidNumber(value);
        }),
    });
  }

  if (buttonType === ButtonType.HANDOVER) {
    return Yup.object().shape({
      ...commonFields,
      appId: Yup.string().trim().required('App Id Required'),
    });
  }

  if (buttonType === ButtonType.PAGE && disregardLayout) {
    return Yup.object().shape({
      ...commonFields,
      pageId: Yup.string().trim().required('Dialogue Required'),
    });
  }

  if (buttonType === ButtonType.URL) {
    return Yup.object().shape({
      ...commonFields,
      url: Yup.string()
        .trim()
        .matches(URL_REGEX, 'Invalid Url')
        .required('Url Required'),
    });
  }

  return Yup.object().shape(commonFields);
};

const formFields = {
  buttonName: 'buttonName',
  phoneNumber: 'phoneNumber',
  url: 'url',
  webviewSize: 'webviewSize',
  appId: 'appId',
  metadata: 'metadata',
  pageId: 'pageId',
  selectedButtonType: 'selectedButtonType',
  tags: 'tags',
};

const validate =
  (disregardLayout?: boolean) => async (values: CreateEditButtonFormValues) => {
    const validationSchema = getValidationSchema(
      values.selectedButtonType,
      disregardLayout,
    );
    let errors = {};

    try {
      await validationSchema.validate(values, { abortEarly: false });
    } catch (e) {
      errors = yupToFormErrors(e);
    }

    return errors;
  };

const FormHelpers = { formFields, validate, getInitialValues };

export default FormHelpers;
